import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Loader2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const roleBadge = {
  admin: { label: "Admin", color: "bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]" },
  vendedor: { label: "Vendedor", color: "bg-emerald-100 text-emerald-700" },
  cobrador: { label: "Cobrador", color: "bg-amber-100 text-amber-700" },
};

export default function StaffLogin() {
  const { business, businessToken, staffLogin, refreshStaffList, switchBusiness, session } = useAuth();
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!businessToken) return;
    refreshStaffList().then(setStaffList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!businessToken) return <Navigate to="/login" replace />;
  if (session) return <Navigate to="/" replace />;

  const quickFill = (uname) => setUsername(uname);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!username || !password) return toast.error("Preencha usuário e senha");
    setLoading(true);
    try {
      await staffLogin(username.toLowerCase().trim(), password);
      toast.success("Bem-vindo!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Usuário ou senha inválidos");
    } finally {
      setLoading(false);
    }
  };

  const asAdmin = () => {
    setUsername("admin");
    setTimeout(() => document.querySelector('[data-testid="input-staff-password"]')?.focus(), 50);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-cream)" }}>
      <div className="flex-1 px-4 py-8 max-w-md mx-auto w-full">
        <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">{business?.name}</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900 mb-1">Quem está trabalhando?</h1>
        <p className="text-sm text-stone-500 mb-5">Etapa 2 de 2 — login do funcionário.</p>

        {staffList.length > 0 && (
          <div className="mb-4 flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="staff-cards">
            {staffList.map((s) => {
              const roles = Object.keys(s.roles || {}).filter((k) => s.roles[k]);
              const primary = roles[0] || "vendedor";
              return (
                <button
                  key={s.id}
                  data-testid={`staff-card-${s.username}`}
                  onClick={() => quickFill(s.username)}
                  className="shrink-0 w-28 carne-card p-3 flex flex-col items-center gap-1 hover:border-[color:var(--terracotta)] transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)] flex items-center justify-center font-display text-xl font-bold">
                    {s.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="text-xs font-bold text-stone-900 text-center truncate w-full">{s.name}</div>
                  <div className="text-[9px] text-stone-500">@{s.username}</div>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${roleBadge[primary]?.color || "bg-stone-100 text-stone-600"}`}>
                    {roleBadge[primary]?.label || primary}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <form onSubmit={submit} className="carne-card p-5 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Usuário</label>
            <input
              data-testid="input-staff-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="ex: joao"
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Senha</label>
            <input
              data-testid="input-staff-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
              required
            />
          </div>
          <button
            data-testid="btn-staff-login"
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Entrar
          </button>
          <button
            data-testid="btn-login-as-admin"
            type="button"
            onClick={asAdmin}
            className="w-full h-11 rounded-xl border border-stone-200 text-sm font-bold text-stone-700 inline-flex items-center justify-center gap-2 hover:bg-stone-50"
          >
            <Shield className="w-4 h-4" /> Entrar como administrador
          </button>
        </form>

        <button
          data-testid="btn-switch-business"
          onClick={switchBusiness}
          className="mt-4 w-full text-xs text-stone-500 hover:text-stone-800 inline-flex items-center justify-center gap-1"
        >
          <ChevronLeft className="w-3 h-3" /> Trocar negócio
        </button>
      </div>
    </div>
  );
}
